import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from './api';
import { MetaProvider, useMeta } from './meta';
import { Avatar, Dropdown, initials } from './components';
import { neighbours, useRecent } from './recent';
import { Bell } from './Bell';
import { useReloadScrollRestore } from './scrollRestore';
import { LoginPage } from './pages/Login';
import { ChangePasswordPage, ConfirmEmailPage, ForgotPasswordPage, InvitePage, ResetPasswordPage, SignupPage, usePublicConfig } from './pages/Auth';
import { LegalPage } from './pages/Legal';
import { TeamTile, TeamsPage, useSwitchTeam } from './pages/Teams';
import { MyViewPage } from './pages/MyView';
import { IssueListPage } from './pages/IssueList';
import { IssuePage } from './pages/Issue';
import { ReportPage } from './pages/Report';
import { BoardPage } from './pages/Board';
import { RoadmapPage, ChangelogPage } from './pages/Roadmap';
import { SummaryPage } from './pages/Summary';
import { AnalyticsPage } from './pages/Analytics';
import { AccountPage } from './pages/Account';
import { ManagePage } from './pages/Manage';
import { OperatorPage } from './pages/Operator';
import type { IssueList, User } from './types';
import { setLanguage, t, tError } from './i18n';

export function App() {
  const location = useLocation();
  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<{ user: User | null }>('/api/session'),
    staleTime: Infinity,
  });
  const publicConfig = usePublicConfig();
  const cfg = publicConfig.data;
  const appName = cfg?.app_name;
  useEffect(() => {
    if (appName) document.title = appName;
  }, [appName]);
  // the account's language (if chosen) applies as soon as the session is known
  const accountLanguage = data?.user?.language;
  useEffect(() => {
    if (accountLanguage) setLanguage(accountLanguage);
  }, [accountLanguage]);
  if (isLoading || publicConfig.isLoading) return <div className="fullpage subtle">{t('Loading…')}</div>;
  const path = location.pathname;
  if (path.startsWith('/invite/')) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  }
  if (path === '/login') return <LoginPage />;
  if (path === '/signup') return <SignupPage />;
  if (path === '/forgot-password') return <ForgotPasswordPage />;
  if (path === '/reset-password') return <ResetPasswordPage />;
  if (path === '/confirm-email') return <ConfirmEmailPage />;
  if (path === '/terms' || path === '/privacy' || path === '/imprint') return <LegalPage page={path.slice(1) as 'terms' | 'privacy' | 'imprint'} />;
  if (!data?.user) {
    return <Navigate to={'/login?return=' + encodeURIComponent(location.pathname + location.search + location.hash)} replace />;
  }
  if (data.user.must_change_password) return <ChangePasswordPage user={data.user} />;
  return (
    <MetaProvider>
      <Layout />
    </MetaProvider>
  );
}

export function ProjectAvatar({ name, size = 22 }: { name: string; size?: number }) {
  const colors = ['#0C66E4', '#1F845A', '#8F7EE7', '#E56910', '#C9372C', '#206A83', '#943D73'];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) | 0;
  return (
    <span className="project-avatar" style={{ width: size, height: size, background: colors[Math.abs(h) % colors.length], fontSize: size * 0.42 }}>
      {initials(name)}
    </span>
  );
}

// simple line icons for the sidebar (same items as Mantis)
const ICONS: Record<string, ReactNode> = {
  myview: <path d="M5 16a7 7 0 1114 0M12 16l3.5-4.5M4 19h16" />,
  issues: <path d="M4 5h16v14H4zM4 9h16M8 13h8M8 16h5" />,
  report: <path d="M14 4l6 6-9 9H5v-6zM12 6l6 6" />,
  changelog: <path d="M4 8h12l-3-3M20 16H8l3 3" />,
  roadmap: <path d="M9 4L4 20M15 4l5 16M12 6v2M12 11v2M12 16v2" />,
  summary: <path d="M5 20V10M10 20V4M15 20v-7M20 20V8M3 20h18" />,
  analytics: <path d="M3 17l5-6 4 4 8-9M14 6h6v6" />,
  manage: <path d="M12 8a4 4 0 100 8 4 4 0 000-8zM12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />,
  teams: <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
};
function SideIcon({ name }: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {ICONS[name]}
    </svg>
  );
}

function ProjectPicker() {
  const { meta, projectId, setProjectId, project } = useMeta();
  const location = useLocation();
  const navigate = useNavigate();
  const current = projectId ? project(projectId) : undefined;

  const changeProject = (id: number) => {
    setProjectId(id);
    const params = new URLSearchParams(location.search);
    if (!params.has('project')) return;
    if (id) params.set('project', String(id));
    else params.delete('project');
    params.delete('page');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
  };

  // tree order: parents followed by their subprojects
  const tree = useMemo(() => {
    const out: { id: number; name: string; depth: number }[] = [];
    const roots = meta.projects.filter((p) => !p.parent_ids.some((pid) => meta.projects.some((x) => x.id === pid)));
    const walk = (id: number, depth: number, seen: Set<number>) => {
      const p = meta.projects.find((x) => x.id === id)!;
      out.push({ id, name: p.name, depth });
      for (const c of meta.projects.filter((x) => x.parent_ids.includes(id))) if (!seen.has(c.id)) walk(c.id, depth + 1, new Set([...seen, c.id]));
    };
    for (const r of roots) walk(r.id, 0, new Set([r.id]));
    return out;
  }, [meta.projects]);

  return (
    <Dropdown
      align="right"
      className="project-picker"
      trigger={(open) => (
        <button
          className={`nav-btn ${open ? 'open' : ''}`}
          // arrow down on the closed button opens the list, like a select
          onKeyDown={(e) => !open && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && (e.preventDefault(), e.currentTarget.click())}
          title={t('Switch project')}
        >
          {current ? <ProjectAvatar name={current.name} size={20} /> : <span className="project-avatar all">∗</span>}
          <span className="pp-name">{current?.name ?? t('All Projects')}</span> ▾
        </button>
      )}
    >
      {(close) => <ProjectPickerMenu tree={tree} projectId={projectId} onPick={(id) => { changeProject(id); close(); }} onClose={close} />}
    </Dropdown>
  );
}

/**
 * The list of the project picker. The focus stays in the search field; the keys move a highlight through the list:
 * up/down, page up/down, Ctrl+Home/End (Home/End too while the search is empty), Enter picks, Escape closes.
 * It opens on the current project.
 */
function ProjectPickerMenu({ tree, projectId, onPick, onClose }: {
  tree: { id: number; name: string; depth: number }[]; projectId: number; onPick: (id: number) => void; onClose: () => void;
}) {
  const [filter, setFilter] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const shown = filter ? tree.filter((p) => p.name.toLowerCase().includes(filter.toLowerCase())) : tree;
  // the choices in order; 0 is "All Projects" (only without a search)
  const ids = filter ? shown.map((p) => p.id) : [0, ...shown.map((p) => p.id)];
  const [cursor, setCursor] = useState(() => Math.max(0, ids.indexOf(projectId)));
  const at = Math.min(cursor, ids.length - 1);

  // keep the highlighted entry in view (below the sticky "All Projects")
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${at}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [at, filter]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const last = ids.length - 1;
    const page = Math.max(1, Math.floor((listRef.current?.clientHeight ?? 360) / 37) - 1);
    const empty = !filter;
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = Math.min(last, at + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, at - 1);
    else if (e.key === 'PageDown') next = Math.min(last, at + page);
    else if (e.key === 'PageUp') next = Math.max(0, at - page);
    else if (e.key === 'Home' && (e.ctrlKey || empty)) next = 0;
    else if (e.key === 'End' && (e.ctrlKey || empty)) next = last;
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (at >= 0 && ids[at] !== undefined) onPick(ids[at]);
      return;
    } else if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (next == null) return;
    e.preventDefault();
    setCursor(next);
  };

  const item = (index: number, id: number, content: React.ReactNode, className = '', style?: React.CSSProperties) => (
    <button
      key={id}
      data-index={index}
      className={`menu-item ${className} ${id === projectId ? 'active' : ''} ${index === at ? 'cursor' : ''}`}
      style={style}
      tabIndex={-1}
      onMouseMove={() => index !== at && setCursor(index)}
      onClick={() => onPick(id)}
    >
      {content}
    </button>
  );
  return (
    <div className="picker-menu">
      <input
        className="picker-search"
        value={filter}
        autoFocus
        role="combobox"
        aria-expanded="true"
        onChange={(e) => {
          setFilter(e.target.value);
          setCursor(0); // the first match
        }}
        onKeyDown={onKeyDown}
        placeholder={t('Search')}
      />
      <div className="picker-list" ref={listRef} role="listbox">
        {/* sticky: stays at the top while the list scrolls */}
        {!filter && item(0, 0, t('All Projects'), 'picker-all')}
        {shown.map((o, i) =>
          item(filter ? i : i + 1, o.id, <>
            {o.depth > 0 && !filter && <span className="subtle">» </span>}
            {o.name}
          </>, '', { paddingLeft: 12 + (filter ? 0 : o.depth * 16) }),
        )}
        {shown.length === 0 && <div className="menu-empty">{t('No matching project')}</div>}
      </div>
    </div>
  );
}

/** Current team in the navbar; opens a quick switcher (the full picker is /teams). */
function TeamSwitcher() {
  const { meta } = useMeta();
  const switchTeam = useSwitchTeam();
  const navigate = useNavigate();
  const team = meta.team;
  return (
    <Dropdown
      className="team-switcher"
      trigger={(open) => (
        <button className={`nav-btn ${open ? 'open' : ''}`} title={t('Switch team')}>
          {team ? <TeamTile name={team.name} size={22} /> : <span className="project-avatar all">∗</span>}
          <span className="pp-name">{team?.name ?? t('No team')}</span> ▾
        </button>
      )}
    >
      {(close) => (
        <div className="picker-menu">
          <div className="picker-list">
            {meta.teams.map((t) => (
              <button
                key={t.id}
                className={`menu-item team-row ${t.id === team?.id ? 'active' : ''}`}
                onClick={() => {
                  close();
                  if (t.id !== team?.id) switchTeam(t.id);
                }}
              >
                <TeamTile name={t.name} size={24} />
                <span className="team-row-name">{t.name}</span>
              </button>
            ))}
          </div>
          <div className="menu-sep" />
          <button
            className="menu-item"
            onClick={() => {
              close();
              navigate('/teams');
            }}
          >
            {t('All teams…')}
          </button>
        </div>
      )}
    </Dropdown>
  );
}

/** Until the e-mail address is confirmed (after sign-up or a change), with a button to send the link again. */
function EmailBanner() {
  const { meta } = useMeta();
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState('');
  if (meta.user.email_confirmed !== false || !meta.user.email) return null;
  return (
    <div className="banner info">
      {t('Please confirm your e-mail address {email}: open the link in the e-mail we sent you.', { email: meta.user.email })}{' '}
      {state === 'sent' ? (
        <strong>{t('Sent. Check your inbox.')}</strong>
      ) : (
        <button
          className="link"
          disabled={state === 'busy'}
          onClick={async () => {
            setState('busy');
            setError('');
            try {
              await api.post('/api/account/email/resend');
              setState('sent');
            } catch (err) {
              setError((err as Error).message);
              setState('idle');
            }
          }}
        >
          {t('Send the link again')}
        </button>
      )}
      {error && <div className="error">{tError(error)}</div>}
    </div>
  );
}

function RecentlyVisited() {
  const ids = useRecent();
  const { data } = useQuery({
    queryKey: ['issues', 'recent', ids],
    queryFn: () => api.get<IssueList>('/api/issues' + qs({ ids: ids.join(','), hide_status: 0, per_page: 10 })),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  if (ids.length === 0) return null;
  const status = new Map((data?.issues ?? []).map((i) => [i.id, i]));
  return (
    <div className="recent">
      <span className="subtle">{t('Recently visited:')}</span>
      {ids.map((id) => {
        const i = status.get(id);
        return (
          <Link key={id} to={`/issues/${id}`} className={i && i.status >= 80 ? 'resolved' : ''} title={i?.summary}>
            {String(id).padStart(7, '0')}
          </Link>
        );
      })}
    </div>
  );
}

function Layout() {
  const { meta, projectId, project, label, th, level } = useMeta();
  const config = usePublicConfig().data;
  const appName = config?.app_name ?? 'BugTracker';
  const mantis = config?.mantis_database === true;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  useReloadScrollRestore();
  const [jump, setJump] = useState('');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('bt.sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const isAdmin = meta.user.access_level >= th('manage_user');
  const canManage = isAdmin || meta.projects.some((p) => p.access >= th('manage_project'));
  const canReport = meta.projects.some((p) => p.access >= th('report_bug'));
  const lvl = projectId ? level(projectId) : meta.user.access_level;
  const displayName = meta.user.realname ? `${meta.user.username} ( ${meta.user.realname} )` : meta.user.username;
  const levelLabel = label('access_level', lvl);
  const levelTitle = projectId
    ? t('Access level {level} in {project}', { level: levelLabel, project: project(projectId)?.name ?? '' })
    : t('Access level {level}', { level: levelLabel });
  const sidebarLabel = collapsed ? t('Expand sidebar') : t('Collapse sidebar');
  const issueMatch = location.pathname.match(/^\/issues\/(\d+)$/);
  const issueNeighbours = issueMatch ? neighbours(Number(issueMatch[1])) : null;

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    try {
      localStorage.setItem('bt.sidebar', collapsed ? 'expanded' : 'collapsed');
    } catch {
      /* ignore */
    }
  };
  const logout = async () => {
    await api.post('/api/logout');
    qc.clear();
    navigate('/login');
  };
  const openRss = async () => {
    const { url } = await api.get<{ url: string }>('/api/account/rss');
    window.open(url + (projectId ? `&project_id=${projectId}` : ''), '_blank', 'noopener');
  };
  const doJump = (e: FormEvent) => {
    e.preventDefault();
    const q = jump.trim().replace(/^#/, '');
    if (/^\d+$/.test(q)) navigate(`/issues/${Number(q)}`);
    else if (q) navigate(`/issues?search=${encodeURIComponent(q)}&hide_status=0`);
    setJump('');
  };

  const items: { to: string; icon: string; label: string; end?: boolean; show?: boolean }[] = [
    { to: '/issues', icon: 'issues', label: t('View Issues') },
    { to: '/', icon: 'myview', label: t('My View'), end: true },
    { to: '/report', icon: 'report', label: t('Report Issue'), show: canReport },
    { to: '/roadmap', icon: 'roadmap', label: t('Roadmap') },
    { to: '/changelog', icon: 'changelog', label: t('Change Log') },
    { to: '/summary', icon: 'summary', label: t('Summary|page') },
    { to: '/analytics', icon: 'analytics', label: t('Projects overview') },
    { to: '/teams', icon: 'teams', label: t('Teams'), show: !mantis },
    { to: '/manage', icon: 'manage', label: t('Manage'), show: canManage },
  ];

  return (
    <div className={`shell ${collapsed ? 'collapsed' : ''}`}>
      <header className="navbar">
        <button
          type="button"
          className="icon-btn sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={sidebarLabel}
          title={sidebarLabel}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
            <path d={collapsed ? 'M13 9l3 3-3 3' : 'M16 9l-3 3 3 3'} />
          </svg>
        </button>
        <Link to="/" className="brand">
          <span className="brand-mark">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <path d="M12 2l9 5v10l-9 5-9-5V7z" fill="#fff" />
              <path d="M12 7l4.5 2.5v5L12 17l-4.5-2.5v-5z" fill="#0C66E4" />
            </svg>
          </span>
          <span className="brand-name">{appName}</span>
        </Link>
        {!mantis && <TeamSwitcher />}
        {issueNeighbours && (
          <div className="issue-nav-controls">
            <button
              className="btn page-scroll"
              disabled={!issueNeighbours.prev}
              onClick={() => issueNeighbours.prev && navigate(`/issues/${issueNeighbours.prev}`)}
              title={t('Previous issue in the list')}
              aria-label={t('Previous issue in the list')}
            >
              «
            </button>
            <button
              className="btn page-scroll"
              disabled={!issueNeighbours.next}
              onClick={() => issueNeighbours.next && navigate(`/issues/${issueNeighbours.next}`)}
              title={t('Next issue in the list')}
              aria-label={t('Next issue in the list')}
            >
              »
            </button>
            <span className="issue-nav-divider" aria-hidden />
            <button
              className="btn page-scroll"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              title={t('Scroll to top')}
              aria-label={t('Scroll to top')}
            >
              ↑
            </button>
            <button
              className="btn page-scroll"
              onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}
              title={t('Scroll to bottom')}
              aria-label={t('Scroll to bottom')}
            >
              ↓
            </button>
          </div>
        )}
        <div className="navbar-right">
          {canReport && (
            <Link to="/report" className="btn primary">
              + {t('Report Issue')}
            </Link>
          )}
          <RecentlyVisited />
          <form onSubmit={doJump} className="jump-box">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M20 20l-4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input value={jump} onChange={(e) => setJump(e.target.value)} placeholder={t('Issue #')} aria-label={t('Jump to issue')} />
          </form>
          <ProjectPicker />
          <Bell />
          <Dropdown
            align="right"
            trigger={(open) => (
              <button className={`nav-btn ${open ? 'open' : ''}`} title={displayName}>
                <Avatar name={meta.user.realname || meta.user.username} size={24} />
                <span className="pp-name user-name">{displayName}</span> ▾
              </button>
            )}
          >
            {(close) => (
              <div className="user-menu">
                <div className="user-menu-head">
                  <strong>{displayName}</strong>
                  <span className="subtle small">{levelTitle}</span>
                </div>
                <div className="menu-sep" />
                <Link className="menu-item" to="/account" onClick={close}>
                  {t('My Account')}
                </Link>
                {meta.user.site_admin && (
                  <Link className="menu-item" to="/operator" onClick={close}>
                    {t('Site administration')}
                  </Link>
                )}
                <button
                  className="menu-item"
                  onClick={() => {
                    close();
                    openRss();
                  }}
                >
                  {t('RSS')}
                </button>
                <div className="menu-sep" />
                <button className="menu-item" onClick={logout}>
                  {t('Logout')}
                </button>
              </div>
            )}
          </Dropdown>
        </div>
      </header>

      <aside id="app-sidebar" className="sidebar">
        <nav>
          {items
            .filter((i) => i.show !== false)
            .map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} title={i.label} aria-label={i.label}>
                <SideIcon name={i.icon} />
                <span className="side-label">{i.label}</span>
              </NavLink>
            ))}
        </nav>
      </aside>

      <div className="main-col">
        <main className="content">
          <EmailBanner />
          {!meta.team && location.pathname !== '/teams' && location.pathname !== '/account' ? (
            <Navigate to="/teams" replace />
          ) : (
          <Routes>
            <Route path="/teams" element={mantis ? <Navigate to="/manage/team" replace /> : <TeamsPage />} />
            <Route path="/" element={<MyViewPage />} />
            <Route path="/issues" element={<IssueListPage />} />
            <Route path="/board" element={<BoardPage />} />
            <Route path="/issues/:id" element={<IssuePage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/summary" element={<SummaryPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/manage/*" element={<ManagePage />} />
            {meta.user.site_admin && <Route path="/operator" element={<OperatorPage />} />}
            <Route
              path="*"
              element={
                <div className="empty-state">
                  <h2>{t('Page not found')}</h2>
                  <Link to="/">{t('Back to My View')}</Link>
                </div>
              }
            />
          </Routes>
          )}
        </main>
      </div>
    </div>
  );
}
